import { createRouter as createTanstackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { useEffect } from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { InactivityTimeoutProvider } from "./components/auth/InactivityTimeoutProvider";
import { DatadogInitializer } from "./components/DatadogInitializer";
import { MixpanelInitializer } from "./services/mixpanel-initializer";
import { AppContextProvider } from "./contexts";
import { AuthProvider } from "./contexts/AuthProvider";
import i18next from "./i18n";
import { ApiInitializer, ApiProvider } from "./integrations/api/provider";
import * as TanstackQuery from "./integrations/tanstack-query/root-provider";
import { registerAppRouter } from "./lib/app-router";

// ✅ Auth context removed - route guards now use direct Zustand access

// Create a placeholder config context for initial router setup
const initialConfigContext: ConfigRouterContext =
	createConfigRouterContext(null);

import {
	ConfigProvider,
	type ConfigRouterContext,
	createConfigRouterContext,
	useConfiguration,
} from "./contexts/ConfigProvider";
import { FieldInfoLabelsProvider } from "./contexts/FieldInfoLabelsProvider";
import { ThemeProvider } from "./contexts/ThemeProvider";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { authStore } from "./stores/auth-store";
import { logApp } from "./utils/logApp";
import { installMissingChunkReloadListener } from "./utils/reload-on-missing-chunk";

// Create a new router instance
export const createRouter = () => {
	installMissingChunkReloadListener();
	const rqContext = TanstackQuery.getContext();

	const router = createTanstackRouter({
		routeTree,
		context: {
			...rqContext,
			// Config context starts with initial state and gets updated by RouterContextProvider
			config: initialConfigContext,
			// Translation function for use in route guards and beforeLoad
			t: i18next.t.bind(i18next),
		},
		defaultPreload: "intent",
		Wrap: (props: { children: React.ReactNode }) => {
			return (
				<TanstackQuery.Provider {...rqContext}>
					<ThemeProvider>
						<ConfigProvider>
							<ApiProvider>
								<AuthProvider>
									<InactivityTimeoutProvider>
										<FieldInfoLabelsProvider>
											{/* biome-ignore lint/suspicious/noExplicitAny: <false positive> */}
											<RouterContextProvider router={router as any}>
												<AppContextProvider>
													<DatadogInitializer />
													<MixpanelInitializer />
													<ApiInitializer>{props.children}</ApiInitializer>
												</AppContextProvider>
											</RouterContextProvider>
										</FieldInfoLabelsProvider>
									</InactivityTimeoutProvider>
								</AuthProvider>
							</ApiProvider>
						</ConfigProvider>
					</ThemeProvider>
				</TanstackQuery.Provider>
			);
		},
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient: rqContext.queryClient,
	});

	registerAppRouter(router);

	return router;
};

/**
 * Router Context Provider
 *
 * This component provides the config and translation contexts to the router.
 * Auth context has been removed - route guards now use direct Zustand access.
 */
function RouterContextProvider({
	children,
	router,
}: {
	children: React.ReactNode;
	// biome-ignore lint/suspicious/noExplicitAny: <false positive>
	router: any;
}) {
	const config = useConfiguration();
	const { i18n } = useAppTranslation();

	/**
	 * Update the router context whenever config state changes
	 * Auth context removed - route guards use direct Zustand access for better reliability
	 */
	useEffect(() => {
		if (router && config) {
			// Create config router context from current config state
			const configRouterContext = createConfigRouterContext(config);
			const organizationId = router.state.matches[2]?.params?.orgId;
			if (organizationId) {
				authStore.getState().setActiveOrganization(organizationId);
			}

			// Use router.update to inject the current config context
			try {
				router.update({
					context: {
						...router.options.context,
						config: configRouterContext,
						t: i18next.t.bind(i18next),
						activeOrganizationId: authStore.getState().activeOrganizationId,
					},
				});
			} catch (error) {
				console.warn("Failed to update router context:", error);
				logApp({
					level: "error",
					message: "Failed to update router context",
				});
			}
		}
	}, [router, config]);

	/**
	 * Update translation function when language changes
	 * This ensures route guards always use the current language
	 */
	useEffect(() => {
		const handleLanguageChange = () => {
			if (router) {
				try {
					router.update({
						context: {
							...router.options.context,
							t: i18next.t.bind(i18next),
						},
					});
					console.log("🌐 Router context updated with new language");
				} catch (error) {
					console.warn(
						"Failed to update translation in router context:",
						error,
					);
					logApp({
						level: "error",
						message: "Failed to update translation in router context",
					});
				}
			}
		};

		i18n.on("languageChanged", handleLanguageChange);
		return () => {
			i18n.off("languageChanged", handleLanguageChange);
		};
	}, [router, i18n]);

	return <>{children}</>;
}

// Export getRouter function for TanStack Start v1.132.4+ compatibility
export const getRouter = createRouter;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createRouter>;
	}
}
